import { Static } from '@adaptiva/core/common';
import { IClassNameDisplay, IOnGenerate, ISummarizable, Summarize } from '@adaptiva/core/summary';
import { CloudStorageType, EBuiltInIds, EJavaClassNames, ServerObject } from '@adaptiva/data-service';
import { CustomerServerAPI } from '../../../namespaces/';
import { customerServerAdaptivaCdnStatus } from '../../../summarize/';
import { ICustomerServer } from '../../interfaces/';

@Static<ISummarizable>()
export class CustomerServer extends ServerObject implements ICustomerServer, IOnGenerate {

	public static override readonly classNameDisplay: IClassNameDisplay = {
		singular: 'Customer Server',
		plural: 'Customer Servers'
	};

	public static readonly className: string = EJavaClassNames.CustomerServer;

	public static API = CustomerServerAPI;

	public override javaClassName: string = EJavaClassNames.CustomerServer;

	// customer name, provided by the Adaptiva support team while creating this object
	@Summarize({
		type: 'string',
		columnDisplay: 'Customer Name',
		isHeading: true
	})
	public customerName: string;

	// tenant id that has been assigned to this server
	// automatically allocated and filled out by the system when the server is activated
	@Summarize({
		type: 'number',
		columnDisplay: 'Tenant ID'
	})
	public tenantId: number;

	// automatically filled out by the system when the object is first created
	// upper 32 bits set to tenant id, lower 32 bits set to 0
	@Summarize({
		type: 'number',
		columnDisplay: 'Globally Unique ID'
	})
	public globallyUniqueId: BigInt;

	// public key for this server, from its signed certificate issued by Adaptiva
	// automatically generated by the system when the object is first created
	public publicKey: number[];

	// public key encoded as a Base64Url string:
	// automatically generated and filled out by the system when the server is activated:
	// When the object is first created, this field is temporarily filled out with the activation code, until the server is activated.
	// During activation, it is overwritten with the actual public key for this server, which will be part of its Adaptiva-signed certificate
	public encodedPublicKey: string;

	// the Adaptiva server guid
	// automatically filled out by the system when the server is activated
	@Summarize({
		type: 'string',
		columnDisplay: 'Server GUID'
	})
	public serverGuid: string;

	// SQL date-time: contains the date and time when the server was activated
	// automatically generated and filled out by the system when the server is activated
	@Summarize({
		type: 'timestamp',
		columnDisplay: 'Activation Time',
		isHeading: true
	})
	public activationTime: string;

	@Summarize({
		type: 'number',
		columnDisplay: 'Customer Object ID'
	})
	public customerObjectId: number;

	// The latest reason a particular task failed
	@Summarize({
		type: 'string',
		columnDisplay: 'Task Failure Reason'
	})
	public taskFailureReason: string;

	public provisionStorageTaskStatus: byte = CustomerServer.API.TASK_STATUS_NOT_STARTED;

	public provisionCdnTaskStatus: byte = CustomerServer.API.TASK_STATUS_NOT_STARTED;

	public provisionDnsTaskStatus: byte = CustomerServer.API.TASK_STATUS_NOT_STARTED;

	public cdnCloudStorageRegion: string = 'NY';

	public reasonForDeactivation: string = 'Account has been deactivated for an un-specified reason';

	@Summarize({
		type: 'string',
		columnDisplay: 'Adaptiva CDN Status',
		isHeading: true,
		transformers: {
			toDisplay: customerServerAdaptivaCdnStatus
		}
	})
	public adaptivaCdnStatus = CustomerServer.API.ADAPTIVA_CDN_STATUS_NOT_PROVISIONED;

	public adaptivaCdnStorageType = CloudStorageType.BUNNY;

	@Summarize({
		type: 'boolean',
		columnDisplay: 'SSL Enabled'
	})
	public enableCdnSslSecurity = true;

	public parentFolderId: number = EBuiltInIds.ROOT_FOLDER_CUSTOMER_SERVER_OBJECT;

	constructor(_from?: Partial<ICustomerServer>) {
		super();
		if (_from)
			Object.assign<ICustomerServer, Partial<ICustomerServer>>(this, _from);
	}

	public override toJSON(): this {
		const _serialized: any = Object.assign({}, this);
		// globallyUniqueId is a 64-bit long that actually uses all of its bits
		// so we need to use BigInt and stringify it since JS can't handle that many bits
		if (typeof _serialized.globallyUniqueId?.toString === 'function')
			_serialized.globallyUniqueId = _serialized.globallyUniqueId.toString();

		return _serialized;
	}

	public onGenerate(): void {
		if (typeof this.globallyUniqueId === 'number')
			this.globallyUniqueId = BigInt(this.globallyUniqueId || 0);
	}

}
